import api from './axiosApi'

// Types matching backend OpenAPI spec
export type HierarchyLevel = 'DIVISION' | 'DEPARTMENT' | 'CLASS' | 'SUBCLASS'

export interface HierarchyNodeRead {
  id: string
  code: string
  name: string
  level: HierarchyLevel
  parent_id: string | null
  is_active: boolean
}

// Tree node structure from /hierarchies/tree endpoint
export interface HierarchyTreeNode {
  id: string
  code: string
  name: string
  level: HierarchyLevel
  children?: HierarchyTreeNode[]
}

interface HierarchyTreeResponse {
  data: HierarchyTreeNode[]
}

// Cache for the hierarchy tree to avoid repeated API calls
let hierarchyTreeCache: HierarchyTreeNode[] | null = null

/**
 * Fetch the full hierarchy tree from the API
 * Results are cached in memory for the session
 */
export async function fetchHierarchyTree(): Promise<HierarchyTreeNode[]> {
  if (hierarchyTreeCache) {
    return hierarchyTreeCache
  }

  const response = await api.get<HierarchyTreeResponse>('/hierarchies/tree', {
    params: { max_depth: 4 },
  })
  hierarchyTreeCache = response.data.data
  return hierarchyTreeCache
}

/**
 * Clear the hierarchy tree cache (useful for testing or forced refresh)
 */
export function clearHierarchyCache(): void {
  hierarchyTreeCache = null
}

/**
 * Convert a tree node to flat HierarchyNodeRead format
 */
function treeNodeToFlat(
  node: HierarchyTreeNode,
  parentId: string | null
): HierarchyNodeRead {
  return {
    id: node.id,
    code: node.code,
    name: node.name,
    level: node.level,
    parent_id: parentId,
    is_active: true,
  }
}

/**
 * Find nodes at a specific level in the tree
 */
function findNodesAtLevel(
  nodes: HierarchyTreeNode[],
  targetLevel: HierarchyLevel,
  parentId: string | null = null
): HierarchyNodeRead[] {
  const result: HierarchyNodeRead[] = []

  for (const node of nodes) {
    if (node.level === targetLevel) {
      result.push(treeNodeToFlat(node, parentId))
    }
    if (node.children) {
      result.push(...findNodesAtLevel(node.children, targetLevel, node.id))
    }
  }

  return result
}

/**
 * Find children of a specific parent node
 */
function findChildrenOfParent(
  nodes: HierarchyTreeNode[],
  parentId: string
): HierarchyNodeRead[] {
  for (const node of nodes) {
    if (node.id === parentId) {
      return (node.children || []).map(child => treeNodeToFlat(child, parentId))
    }
    if (node.children) {
      const found = findChildrenOfParent(node.children, parentId)
      if (found.length > 0) return found
    }
  }
  return []
}

/**
 * Fetch hierarchy nodes by level and optional parent
 * Uses the cached tree data for filtering
 */
export async function fetchHierarchyByLevel(
  level: HierarchyLevel,
  parentId?: string
): Promise<HierarchyNodeRead[]> {
  const tree = await fetchHierarchyTree()

  let results: HierarchyNodeRead[]

  // If looking for departments (or divisions), return top-level nodes of that level
  if (level === 'DEPARTMENT' || level === 'DIVISION') {
    results = findNodesAtLevel(tree, level)
  } else if (parentId) {
    // For class/subclass, we need the parent
    results = findChildrenOfParent(tree, parentId)
  } else {
    // Fallback: return all nodes at that level
    results = findNodesAtLevel(tree, level)
  }

  // Sort by code using zero-padded comparison (handles numeric strings safely)
  return results.sort((a, b) =>
    a.code.padStart(10, '0').localeCompare(b.code.padStart(10, '0'))
  )
}

/**
 * Find a specific node by ID in the tree
 */
function findNodeById(
  nodes: HierarchyTreeNode[],
  id: string,
  parentId: string | null = null
): HierarchyNodeRead | null {
  for (const node of nodes) {
    if (node.id === id) {
      return treeNodeToFlat(node, parentId)
    }
    if (node.children) {
      const found = findNodeById(node.children, id, node.id)
      if (found) return found
    }
  }
  return null
}

/**
 * Get a hierarchy node by ID
 */
export async function fetchHierarchyNode(
  id: string
): Promise<HierarchyNodeRead | null> {
  const tree = await fetchHierarchyTree()
  return findNodeById(tree, id)
}

export interface HierarchyPath {
  departmentId: string | null
  classId: string | null
  subclassId: string | null
}

/**
 * Find the path from root to a target node
 */
function findPathToNode(
  nodes: HierarchyTreeNode[],
  targetId: string,
  currentPath: HierarchyTreeNode[]
): HierarchyTreeNode[] {
  for (const node of nodes) {
    if (node.id === targetId) {
      return [...currentPath, node]
    }
    if (node.children) {
      const found = findPathToNode(node.children, targetId, [
        ...currentPath,
        node,
      ])
      if (found.length > 0) return found
    }
  }
  return []
}

/**
 * Find the full hierarchy path (department → class → subclass) for a given node ID
 */
export async function findHierarchyPath(
  nodeId: string
): Promise<HierarchyPath> {
  const tree = await fetchHierarchyTree()
  const path = findPathToNode(tree, nodeId, [])

  return {
    departmentId: path.find(n => n.level === 'DEPARTMENT')?.id ?? null,
    classId: path.find(n => n.level === 'CLASS')?.id ?? null,
    subclassId: path.find(n => n.level === 'SUBCLASS')?.id ?? null,
  }
}
